import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '@/hooks/use-api-query';
import { qk } from '@/lib/query/keys';

export type PersistentChannelType = 'whatsapp' | 'instagram' | 'facebook';

export interface PersistentLink {
  publicId: string;
  url: string;
  successRedirectUrl: string | null;
  failureRedirectUrl: string | null;
}

export interface OrgDestination {
  webhookUrl: string | null;
  verifyToken: string | null;
  // Org-default HMAC signing secret. Distinct from verifyToken.
  hmacSecret: string | null;
}

interface SavePersistentRedirectsVars {
  successRedirectUrl: string;
  failureRedirectUrl: string;
}

interface SaveOrgDestinationVars {
  webhookUrl: string;
  verifyToken: string;
}

function persistentLinkKey(orgId: string, channelType: PersistentChannelType) {
  return qk.org(orgId, 'onboarding-links', 'persistent', channelType);
}

function orgDestinationKey(orgId: string) {
  return qk.org(orgId, 'destination');
}

function orgDestinationConfigKey(orgId: string) {
  return qk.org(orgId, 'destination', 'config');
}

export function useOrgDestinationConfig(orgId: string) {
  return useApiQuery<OrgDestination>(
    orgDestinationConfigKey(orgId),
    `/organizations/${orgId}/destination`,
    { enabled: !!orgId },
  );
}

export function useSaveOrgDestination(orgId: string) {
  const qc = useQueryClient();
  // PUT returns 204 No Content — no body to read. Invalidate so the config
  // (incl. the server-minted HMAC) and destination summary refetch.
  return useApiMutation<void, SaveOrgDestinationVars>(
    (vars) => ({
      path: `/organizations/${orgId}/destination`,
      init: {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vars),
      },
      fallback: 'Failed to save destination',
    }),
    {
      onSuccess: async () => {
        await Promise.all([
          qc.invalidateQueries({ queryKey: orgDestinationConfigKey(orgId) }),
          qc.invalidateQueries({ queryKey: orgDestinationKey(orgId) }),
        ]);
      },
    },
  );
}

export function useClearOrgDestination(orgId: string) {
  const qc = useQueryClient();
  return useApiMutation<void, void>(
    () => ({
      path: `/organizations/${orgId}/destination`,
      init: { method: 'DELETE' },
      fallback: 'Failed to clear destination',
    }),
    {
      onSuccess: () => {
        qc.setQueryData<OrgDestination>(orgDestinationConfigKey(orgId), {
          webhookUrl: null,
          verifyToken: null,
          hmacSecret: null,
        });
        qc.setQueryData<{ webhookUrl: string | null }>(orgDestinationKey(orgId), (prev) => ({
          ...prev,
          webhookUrl: null,
        }));
      },
    },
  );
}

export function useRotateOrgDestinationHmac(orgId: string) {
  const qc = useQueryClient();
  return useApiMutation<{ hmacSecret: string }, void>(
    () => ({
      path: `/organizations/${orgId}/destination/rotate-hmac`,
      init: { method: 'POST' },
      fallback: 'Failed to rotate HMAC secret',
    }),
    {
      onSuccess: (data) => {
        qc.setQueryData<OrgDestination>(orgDestinationConfigKey(orgId), (prev) => ({
          webhookUrl: prev?.webhookUrl ?? null,
          verifyToken: prev?.verifyToken ?? null,
          hmacSecret: data.hmacSecret,
        }));
      },
    },
  );
}

export function usePersistentLink(orgId: string, channelType: PersistentChannelType) {
  return useApiQuery<PersistentLink>(
    persistentLinkKey(orgId, channelType),
    `/org/onboarding-links/persistent/${channelType}`,
    { enabled: !!orgId },
  );
}

export function useRegeneratePersistentLink(
  orgId: string,
  channelType: PersistentChannelType,
) {
  const qc = useQueryClient();
  return useApiMutation<PersistentLink, void>(
    () => ({
      path: `/org/onboarding-links/persistent/${channelType}/regenerate`,
      init: { method: 'POST' },
      fallback: 'Failed to regenerate link',
    }),
    {
      onSuccess: (data) => {
        qc.setQueryData<PersistentLink>(
          persistentLinkKey(orgId, channelType),
          (prev) => ({
            publicId: data.publicId,
            url: data.url,
            successRedirectUrl:
              data.successRedirectUrl ?? prev?.successRedirectUrl ?? null,
            failureRedirectUrl:
              data.failureRedirectUrl ?? prev?.failureRedirectUrl ?? null,
          }),
        );
      },
    },
  );
}

export function useSavePersistentRedirects(
  orgId: string,
  channelType: PersistentChannelType,
) {
  const qc = useQueryClient();
  return useApiMutation<PersistentLink, SavePersistentRedirectsVars>(
    (vars) => ({
      path: `/org/onboarding-links/persistent/${channelType}`,
      init: {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vars),
      },
      fallback: 'Failed to save redirect URLs',
    }),
    {
      onSuccess: (data) => {
        qc.setQueryData<PersistentLink>(persistentLinkKey(orgId, channelType), data);
      },
    },
  );
}
